import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"

import { cn } from "@/lib/utils"

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "smx-flex smx-h-10 smx-w-full smx-items-center smx-justify-between smx-rounded-md smx-border smx-border-input smx-bg-background smx-px-3 smx-py-2 smx-text-sm smx-ring-offset-background placeholder:smx-text-muted-foreground focus:smx-outline-none focus:smx-ring-2 focus:smx-ring-ring focus:smx-ring-offset-2 disabled:smx-cursor-not-allowed disabled:smx-opacity-50 [&>span]:smx-line-clamp-1",
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="smx-h-4 smx-w-4 smx-opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "smx-flex smx-cursor-default smx-items-center smx-justify-center smx-py-1",
      className
    )}
    {...props}
  >
    <ChevronUp className="smx-h-4 smx-w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "smx-flex smx-cursor-default smx-items-center smx-justify-center smx-py-1",
      className
    )}
    {...props}
  >
    <ChevronDown className="smx-h-4 smx-w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "smx-relative smx-z-wordpress-dropdown smx-max-h-96 smx-min-w-[8rem] smx-overflow-hidden smx-rounded-md smx-border smx-bg-popover smx-text-popover-foreground smx-shadow-md data-[state=open]:smx-animate-in data-[state=closed]:smx-animate-out data-[state=closed]:smx-fade-out-0 data-[state=open]:smx-fade-in-0 data-[state=closed]:smx-zoom-out-95 data-[state=open]:smx-zoom-in-95 data-[side=bottom]:smx-slide-in-from-top-2 data-[side=left]:smx-slide-in-from-right-2 data-[side=right]:smx-slide-in-from-left-2 data-[side=top]:smx-slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:smx-translate-y-1 data-[side=left]:smx--translate-x-1 data-[side=right]:smx-translate-x-1 data-[side=top]:smx--translate-y-1",
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "smx-p-1",
          position === "popper" &&
            "smx-h-[var(--radix-select-trigger-height)] smx-w-full smx-min-w-[var(--radix-select-trigger-width)]"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("smx-py-1.5 smx-pl-8 smx-pr-2 smx-text-sm smx-font-semibold", className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "smx-relative smx-flex smx-w-full smx-cursor-default smx-select-none smx-items-center smx-rounded-sm smx-py-1.5 smx-pl-8 smx-pr-2 smx-text-sm smx-outline-none focus:smx-bg-accent focus:smx-text-accent-foreground data-[disabled]:smx-pointer-events-none data-[disabled]:smx-opacity-50",
      className
    )}
    {...props}
  >
    <span className="smx-absolute smx-left-2 smx-flex smx-h-3.5 smx-w-3.5 smx-items-center smx-justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="smx-h-4 smx-w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("smx--mx-1 smx-my-1 smx-h-px smx-bg-muted", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
